import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import config from "config";

import { useWorkDeskUrl } from "scenes/Hubly/components/Workspace/components/hooks/useWorkDeskUrl";

const PARENT_ORIGIN_MESSAGE_TYPE = "PARENT_ORIGIN";
const CLIENT_CARD_URL_CHANGE_MESSAGE_TYPE = "CLIENT_CARD_URL_CHANGE";
const REQUEST_PARENT_ORIGIN_MESSAGE_TYPE = "REQUEST_PARENT_ORIGIN";
const CLIENT_CARD_URL_FROM_PARENT_MESSAGE_TYPE = "CLIENT_CARD_URL_FROM_PARENT";
const WORK_SESSION_COMMAND_MESSAGE_TYPE = "CLIENT_CARD_WORK_SESSION_COMMAND";

function getClientCardOrigin() {
  try {
    return new URL(config.clientCardUrl).origin;
  } catch {
    return null;
  }
}

const identityPath = (pathname) => {
  return pathname;
};

/**
 * @param {object} [options]
 * @param {(pathname: string) => string} [options.mapPathnameForIframeSrc]
 *   Map parent pathname to the embedded app's (default: identity). Used for the
 *   frozen boot src AND ongoing parent->iframe sync, so both always agree.
 * @param {(pathname: string) => string} [options.mapPathnameFromChild]
 *   Map child pathname before history.replace (default: identity).
 * @param {boolean} [options.syncEnabled=true]
 *   When false, bidirectional URL sync is suspended: the parent does not post
 *   `CLIENT_CARD_URL_FROM_PARENT` to the iframe, and the parent ignores
 *   `CLIENT_CARD_URL_CHANGE` from the iframe. The iframe stays mounted and
 *   warm; only URL sync pauses. When flipped back to true, the parent's
 *   current URL is posted to the iframe so it catches up.
 * @param {{id: number, command: string}} [options.sessionCommand]
 *   Work-session command for the embedded app ("reset" | "prefetch"); each
 *   new id is posted once to the iframe.
 */
export function useClientCardIframeSync(options = {}) {
  const {
    mapPathnameForIframeSrc = identityPath,
    mapPathnameFromChild = identityPath,
    syncEnabled = true,
    sessionCommand,
  } = options;
  const history = useHistory();
  const iframeRef = useRef(null);
  const location = useLocation();
  const { embedsClientCardIframe } = useWorkDeskUrl();
  const skipNextParentToIframeSyncRef = useRef(false);

  // lazy init: frozen at mount so the history.replace that URL sync performs
  // cannot change src and reload the iframe mid-session
  const [iframeSrc] = useState(() => {
    const pathname = mapPathnameForIframeSrc(location.pathname);
    // only an embedded view's search describes the embedded app's own state
    const search = embedsClientCardIframe(location.pathname)
      ? location.search
      : "";

    return `${config.clientCardUrl}${pathname}${search}`;
  });

  const handleMessage = useCallback(
    (event) => {
      const clientCardOrigin = getClientCardOrigin();

      if (!clientCardOrigin || event.origin !== clientCardOrigin) return;
      if (
        event.data?.type === REQUEST_PARENT_ORIGIN_MESSAGE_TYPE &&
        event.source
      ) {
        event.source.postMessage(
          { type: PARENT_ORIGIN_MESSAGE_TYPE, origin: window.location.origin },
          event.origin
        );
        return;
      }
      if (event.data?.type !== CLIENT_CARD_URL_CHANGE_MESSAGE_TYPE) return;

      if (!syncEnabled) return;

      // Ignore iframe-initiated URL changes when the parent is not on an
      // embedded path. This prevents the preloaded iframe's internal
      // navigations (e.g. auto-selecting the first client on boot) from
      // yanking the user's browser URL while they're on an unrelated page.
      if (!embedsClientCardIframe(location.pathname)) return;

      const { pathname, search } = event.data;
      if (typeof pathname !== "string") return;

      const searchStr = typeof search === "string" ? search : "";
      const mapped = mapPathnameFromChild(pathname);
      const url = mapped + searchStr;
      const current =
        location.pathname + (location.search ? location.search : "");
      if (url === current) return;

      skipNextParentToIframeSyncRef.current = true;
      history.replace(url);
    },
    [
      history,
      location.pathname,
      location.search,
      mapPathnameFromChild,
      syncEnabled,
      embedsClientCardIframe,
    ]
  );

  useEffect(() => {
    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [history, handleMessage]);

  useEffect(() => {
    if (skipNextParentToIframeSyncRef.current) {
      skipNextParentToIframeSyncRef.current = false;
      return;
    }

    if (!syncEnabled) return;

    const iframe = iframeRef.current;
    const clientCardOrigin = getClientCardOrigin();

    if (
      !iframe?.contentWindow ||
      !clientCardOrigin ||
      !embedsClientCardIframe(location.pathname)
    ) {
      return;
    }

    iframe.contentWindow.postMessage(
      {
        type: CLIENT_CARD_URL_FROM_PARENT_MESSAGE_TYPE,
        pathname: mapPathnameForIframeSrc(location.pathname),
        search: location.search || "",
      },
      clientCardOrigin
    );
  }, [
    location.pathname,
    location.search,
    mapPathnameForIframeSrc,
    syncEnabled,
    embedsClientCardIframe,
  ]);

  // lazy-init: a remounted hook must adopt an inherited command as already
  // consumed instead of replaying it into the fresh iframe
  const lastSessionCommandIdRef = useRef();
  if (lastSessionCommandIdRef.current === undefined) {
    lastSessionCommandIdRef.current = sessionCommand?.id ?? 0;
  }

  const postPendingSessionCommand = useCallback(() => {
    if (
      !sessionCommand ||
      sessionCommand.id === lastSessionCommandIdRef.current
    ) {
      return;
    }

    const iframe = iframeRef.current;
    const clientCardOrigin = getClientCardOrigin();
    if (!iframe?.contentWindow || !clientCardOrigin) return;

    // consume only after the guard so an undeliverable command stays
    // pending and is retried by the next effect pass or iframe load
    lastSessionCommandIdRef.current = sessionCommand.id;
    iframe.contentWindow.postMessage(
      {
        type: WORK_SESSION_COMMAND_MESSAGE_TYPE,
        command: sessionCommand.command,
      },
      clientCardOrigin
    );
  }, [sessionCommand]);

  useEffect(() => {
    postPendingSessionCommand();
  }, [postPendingSessionCommand]);

  const handleIframeLoad = useCallback(() => {
    const iframe = iframeRef.current;
    const clientCardOrigin = getClientCardOrigin();

    if (iframe?.contentWindow && clientCardOrigin) {
      iframe.contentWindow.postMessage(
        { type: PARENT_ORIGIN_MESSAGE_TYPE, origin: window.location.origin },
        clientCardOrigin
      );
      postPendingSessionCommand();
      if (syncEnabled && embedsClientCardIframe(location.pathname)) {
        iframe.contentWindow.postMessage(
          {
            type: CLIENT_CARD_URL_FROM_PARENT_MESSAGE_TYPE,
            pathname: mapPathnameForIframeSrc(location.pathname),
            search: location.search || "",
          },
          clientCardOrigin
        );
      }
    }
  }, [
    location.pathname,
    location.search,
    mapPathnameForIframeSrc,
    postPendingSessionCommand,
    syncEnabled,
    embedsClientCardIframe,
  ]);

  return { iframeRef, iframeSrc, handleIframeLoad };
}

export default useClientCardIframeSync;
